import React, { createContext, useContext, useState, useCallback } from 'react';

const CompareContext = createContext(null);

export const CompareProvider = ({ children }) => {
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const MAX_CANDIDATES = 3;

  const addCandidate = useCallback((candidate) => {
    setSelectedCandidates(prev => {
      // Check if already selected
      if (prev.find(c => c.employee_id === candidate.employee_id)) {
        return prev;
      }
      // Check max limit
      if (prev.length >= MAX_CANDIDATES) {
        return prev;
      }
      return [...prev, {
        employee_id: candidate.employee_id || candidate.id,
        name: candidate.name || `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim(),
        photo_url: candidate.photo_url,
        job_title: candidate.job_title
      }];
    });
  }, []);

  const removeCandidate = useCallback((employeeId) => {
    setSelectedCandidates(prev => prev.filter(c => c.employee_id !== employeeId));
  }, []);

  const toggleCandidate = useCallback((candidate) => {
    const employeeId = candidate.employee_id || candidate.id;
    setSelectedCandidates(prev => {
      const existing = prev.find(c => c.employee_id === employeeId);
      if (existing) {
        return prev.filter(c => c.employee_id !== employeeId);
      }
      if (prev.length >= MAX_CANDIDATES) {
        return prev;
      }
      return [...prev, {
        employee_id: employeeId,
        name: candidate.name || `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim(),
        photo_url: candidate.photo_url,
        job_title: candidate.job_title
      }];
    });
  }, []);

  const isSelected = useCallback((employeeId) => {
    return selectedCandidates.some(c => c.employee_id === employeeId);
  }, [selectedCandidates]);

  const clearAll = useCallback(() => {
    setSelectedCandidates([]);
  }, []);

  const canAddMore = selectedCandidates.length < MAX_CANDIDATES;

  return (
    <CompareContext.Provider value={{
      selectedCandidates,
      addCandidate,
      removeCandidate,
      toggleCandidate,
      isSelected,
      clearAll,
      canAddMore,
      MAX_CANDIDATES
    }}>
      {children}
    </CompareContext.Provider>
  );
};

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error('useCompare must be used within a CompareProvider');
  }
  return context;
};

export default CompareContext;
